import { DatePicker, Image, Input } from 'antd';
import type { Dayjs } from 'dayjs';
import ImageUploader from 'components/ImageUploader';
import dayjs from 'dayjs';
import { t } from 'i18n';

export type OptionalString = string | null | undefined;

export interface UseStepDetailRenderOptions {
    isEditing: boolean;
}

/**
 * 格式化时间字符串
 * @param value 时间字符串
 * @returns 格式化后的时间字符串
 */
export const formatTimeDisplay = (value: OptionalString): OptionalString => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : null);

export function useStepDetailRender({ isEditing }: UseStepDetailRenderOptions) {
    /**
     * 渲染时间行（计划/实际到达或发车时间）
     * @param title 标签文案
     * @param value 显示值（格式化的时间字符串）
     * @param timezone 时区
     * @param draft 编辑态草稿（Dayjs）
     * @param setter 草稿更新函数
     * @param editable 是否可编辑，默认 true
     * @param prefix DatePicker 前缀（如时区缩写）
     * @param color 时间颜色：green=早于计划，red=晚于计划，null=等于或无比较（参考 useTaskCommon.compareTimeColor）
     */
    const renderTimeRow = (
        title: string,
        value: OptionalString,
        timezone: OptionalString,
        draft: Dayjs | null,
        setter: (value: Dayjs | null) => void,
        editable = true,
        prefix?: OptionalString,
        color?: 'green' | 'red' | null
    ) => {
        const colorStyle = color ? { color: color === 'green' ? '#52c41a' : '#ff4d4f' } : {};
        return (
            <div className="flex items-center gap-3 border-b border-gray-100 pb-3" key={title}>
                <span className="text-sm text-gray-500">{title}:</span>
                {isEditing && editable ? (
                    <DatePicker
                        value={draft || undefined}
                        onChange={(date) => setter(date || null)}
                        showTime={{ format: 'HH:mm' }}
                        format="YYYY-MM-DD HH:mm"
                        inputReadOnly
                        prefix={prefix ? <span>{prefix}</span> : undefined}
                    />
                ) : (
                    <span className="text-gray-700" style={colorStyle}>
                        {value ? `${value}${timezone ? ` ${timezone}` : ''}` : '-'}
                    </span>
                )}
            </div>
        );
    };

    /**
     * 渲染照片行（到达/发车照片等）
     * @param label 标签文案
     * @param urls 展示用 URL 列表（只读态）
     * @param draft 编辑态草稿（S3 key 等）
     * @param setDraft 草稿更新函数
     * @param displayUrls 编辑态下 ImageUploader 的展示 URL
     * @param maxCount 最大上传数量，默认 3
     */
    const renderPhotoRow = (
        label: string,
        urls: string[],
        draft: string[],
        setDraft: (v: string[]) => void,
        displayUrls: string[],
        maxCount = 3
    ) => (
        <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
            <span className="text-sm text-gray-500">{label}:</span>
            {isEditing ? (
                <ImageUploader value={draft} displayUrls={displayUrls} onChange={setDraft} maxCount={maxCount} />
            ) : (
                <div className="flex items-center gap-2">
                    {urls.length === 0 ? (
                        <span className="text-gray-700">-</span>
                    ) : (
                        urls.map((photo) => (
                            <Image
                                key={photo}
                                src={photo}
                                width={100}
                                height={100}
                                className="object-cover rounded"
                                preview
                            />
                        ))
                    )}
                </div>
            )}
        </div>
    );

    /**
     * 渲染输入框行（封签号等文本输入）
     * @param label 标签文案
     * @param value 显示值（只读态）
     * @param draft 编辑态草稿
     * @param setDraft 草稿更新函数
     * @param placeholder 占位符
     * @param maxLength 最大长度
     * @param inputClassName Input 的 className
     * @param alphanumericOnly 是否仅允许字母和数字（用于封签号）
     */
    const renderInputRow = (
        label: string,
        value: string,
        draft: string,
        setDraft: (v: string) => void,
        placeholder?: string,
        maxLength?: number,
        inputClassName?: string,
        alphanumericOnly?: boolean
    ) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const v = e.target.value;
            setDraft(alphanumericOnly ? v.replace(/[^A-Za-z0-9]/g, '') : v);
        };
        return (
            <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
                <span className="text-sm text-gray-500">{label}:</span>
                {isEditing ? (
                    <Input
                        value={draft}
                        onChange={handleChange}
                        placeholder={placeholder}
                        maxLength={maxLength}
                        className={inputClassName}
                        showCount
                    />
                ) : (
                    <span className="text-gray-700">{value || '-'}</span>
                )}
            </div>
        );
    };

    /**
     * 渲染备注行：编辑态展示文本框（始终显示）；只读态仅在有值时展示
     * @param label 标签文案
     * @param value 只读态显示值
     * @param draft 编辑态草稿
     * @param setDraft 草稿更新函数
     */
    const renderRemarkRow = (label: string, value: OptionalString, draft: string, setDraft: (v: string) => void) => {
        if (!isEditing && !value) return null;
        return (
            <div className="flex items-center gap-3 border-b border-gray-100 pb-3" key={label}>
                <span className="text-sm text-gray-500 shrink-0">{label}:</span>
                {isEditing ? (
                    <Input.TextArea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder={String(t('common.remarkPlaceholder'))}
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        maxLength={100}
                        showCount
                        allowClear
                        className="w-96"
                        styles={{
                            textarea: { paddingBottom: 18 },
                            count: { position: 'absolute', right: 8, bottom: 0 },
                        }}
                    />
                ) : (
                    <span className="text-gray-700 whitespace-pre-wrap break-all">{value}</span>
                )}
            </div>
        );
    };

    return { renderTimeRow, renderPhotoRow, renderInputRow, renderRemarkRow };
}

